import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Set, Map } from 'immutable';
import { FormattedMessage } from 'react-intl';
import { checkAcceptSignType } from '@/domain/documents/helpers';
import { isSignAvailableSelector } from '@/domain/companies';

import TooltipForButton from '@/components/mui/Tooltip/TooltipForButton';
import Dialog from '@/components/mui/Dialog';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TextFieldBase from '@/components/mui/Form/TextField/TextFieldBase';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import UpdateOutlinedIcon from '@mui/icons-material/UpdateOutlined';

const statusesDefaultMessages = {
  pending: 'Scheduled to Sign',
  signed: 'Signed',
};

const PENDING = 'pending';
const SIGNED = 'signed';

const DialogSignDocument = ({ onSign, onClose, signParams, documents }) => {
  const [isInAction, setIsInAction] = useState(new Map());
  const [signCounter, setSignCounter] = useState(0);

  const isSignAvailable = useSelector(isSignAvailableSelector);

  const signRange = useMemo(() => [1, 2], []);

  const createDefaultPivot = (range) => range.map(() => ({ sign: 0, pending: 0 }));

  const signableDocuments = useMemo(() => {
    if (!Set.isSet(documents)) throw new TypeError(`${JSON.stringify(documents)} is NOT an Immutable Set`);
    return documents.filter((d) => checkAcceptSignType(d.documentID) && !d.protected);
  }, [documents]);

  const signStatus = useMemo(
    () =>
      signableDocuments.reduce(
        (a, v) =>
          signRange.map((id, i) => ({
            // $FlowFixMe
            sign: a[i].sign + v.tags.has(`_S_SIG_DOKKA${id}_SIGNED`),
            // $FlowFixMe
            pending: a[i].pending + v.tags.has(`_S_SIG_DOKKA${id}_PENDING`),
          })),
        createDefaultPivot(signRange),
      ),
    [signRange, signableDocuments],
  );

  const paramsSignCounter = useMemo(() => {
    const status = signStatus;
    return signRange.reduce((a, v, i) => a + (status[i].sign + status[i].pending === signableDocuments.size), 0);
  }, [signRange, signStatus, signableDocuments.size]);

  const getSignatureStatus = useCallback(({ sign, pending }, size) => {
    if (sign === size) return SIGNED;
    if (pending === size) return PENDING;
    if (sign + pending === size) return PENDING;
    return null;
  }, []);

  const signaturesParams = useMemo(() => {
    const signatureStatus = signStatus;
    return signRange.map((e, i) => ({
      token: `token${e}`,
      name: signParams[`token${e}`].name,
      signatureID: e.toString(),
      status: getSignatureStatus(signatureStatus[i], signableDocuments.size),
    }));
  }, [signRange, signStatus, signParams, signableDocuments.size, getSignatureStatus]);

  const isDisabled = useCallback((token) => !isSignAvailable.get(token), [isSignAvailable]);

  const isBeingRequested = useCallback((signatureID) => isInAction.get(signatureID, false), [isInAction]);

  const handleSign = useCallback(
    (id) => {
      const numberOfSignatures = signCounter + 1;
      setSignCounter(numberOfSignatures);
      const max = signRange.length;
      const isLast = numberOfSignatures === max || paramsSignCounter === max;
      setIsInAction(isInAction.set(id, true), onSign(id, isLast));
    },
    [isInAction, onSign, signCounter, paramsSignCounter, signRange.length],
  );

  useEffect(() => {
    setSignCounter(paramsSignCounter);
  }, [paramsSignCounter]);

  if (!signParams.guid) return null;

  return (
    <Dialog
      open
      onClose={onClose}
      onOk={onClose}
      title={<FormattedMessage id="document.sign.title" defaultMessage="Sign Document" />}
    >
      <Stack spacing={2}>
        {signaturesParams.map(({ name, status, signatureID, token }) => {
          const disableSign = isDisabled(token, signatureID);
          return (
            <Stack key={name} direction="row" spacing={2} alignItems="center">
              <TextFieldBase
                readOnly
                value={name}
                slotProps={{ htmlInput: { readOnly: true } }}
                disabled
                size="small"
                fullWidth
              />
              {status !== null && (
                <Stack direction="row" spacing={1} alignItems="center">
                  {status === PENDING && <UpdateOutlinedIcon color="warning" />}
                  {status === SIGNED && <CheckOutlinedIcon color="success" />}

                  <Typography
                    variant="inherit"
                    component="span"
                    sx={{ fontSize: 12 }}
                    noWrap
                    color={status === SIGNED ? 'success.main' : 'warning.main'}
                  >
                    <FormattedMessage id={`document.sign.${status}`} defaultMessage={statusesDefaultMessages[status]} />
                  </Typography>
                </Stack>
              )}
              {status === null && (
                <TooltipForButton
                  t={{
                    id: 'document.sign.disableButtonTooltip',
                    defaultMessage: 'User is not authorized to sign',
                  }}
                  disabled={!disableSign}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    loading={isBeingRequested(signatureID)}
                    disabled={disableSign}
                    onClick={() => handleSign(signatureID)}
                  >
                    <FormattedMessage id="document.sign.signBtn" defaultMessage="Sign" />
                  </Button>
                </TooltipForButton>
              )}
            </Stack>
          );
        })}
      </Stack>
    </Dialog>
  );
};

export default DialogSignDocument;
